/**
 * Bookmark locations — save and recall named map positions (AOIs).
 *
 * Analysts save locations like "Strait of Hormuz", "Suwalki Gap", "Gotland"
 * with a single click, then fly back to them instantly. Stored in localStorage.
 *
 * UI: Map control button opens a dropdown with saved bookmarks + "Save current view".
 */

import { getMap } from '@/map/engine';

const STORAGE_KEY = 'sentinel-bookmarks';

export interface Bookmark {
  id: string;
  name: string;
  center: [number, number]; // [lng, lat]
  zoom: number;
  bearing: number;
  pitch: number;
  createdAt: number;
}

/** Load bookmarks from localStorage. */
function loadBookmarks(): Bookmark[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Bookmark[];
  } catch {
    return [];
  }
}

/** Save bookmarks to localStorage. */
function saveBookmarks(bookmarks: Bookmark[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
  } catch { /* ignore */ }
}

/** Generate a short unique ID. */
function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

/** Add a bookmark for the current map view. */
function addBookmark(name: string): Bookmark | null {
  const map = getMap();
  if (!map) return null;

  const center = map.getCenter();
  const bookmark: Bookmark = {
    id: uid(),
    name,
    center: [center.lng, center.lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
    createdAt: Date.now(),
  };

  const bookmarks = loadBookmarks();
  bookmarks.push(bookmark);
  saveBookmarks(bookmarks);
  return bookmark;
}

/** Delete a bookmark by ID. */
function deleteBookmark(id: string): void {
  const bookmarks = loadBookmarks().filter(b => b.id !== id);
  saveBookmarks(bookmarks);
}

/** Fly to a bookmark. */
function flyToBookmark(bookmark: Bookmark): void {
  const map = getMap();
  if (!map) return;
  map.flyTo({
    center: bookmark.center,
    zoom: bookmark.zoom,
    bearing: bookmark.bearing,
    pitch: bookmark.pitch,
    duration: 1500,
  });
}

let dropdown: HTMLElement | null = null;

/** Close the bookmark dropdown if open. */
function closeDropdown(): void {
  if (dropdown) {
    dropdown.remove();
    dropdown = null;
  }
  document.removeEventListener('click', onOutsideClick);
}

function onOutsideClick(e: MouseEvent): void {
  if (dropdown && !dropdown.contains(e.target as Node)) {
    const btn = document.querySelector('.bookmark-tool-btn');
    if (btn && btn.contains(e.target as Node)) return;
    closeDropdown();
  }
}

/** Render the bookmark dropdown contents. */
function renderDropdown(container: HTMLElement): void {
  container.innerHTML = '';
  const bookmarks = loadBookmarks();

  // Header
  const header = document.createElement('div');
  header.className = 'bookmark-dropdown-header';
  header.textContent = 'Saved Locations';
  container.appendChild(header);

  // Bookmark list
  if (bookmarks.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'bookmark-empty';
    empty.textContent = 'No saved locations';
    container.appendChild(empty);
  } else {
    for (const bm of bookmarks) {
      const row = document.createElement('div');
      row.className = 'bookmark-row';

      const nameEl = document.createElement('button');
      nameEl.className = 'bookmark-name';
      nameEl.textContent = bm.name;
      nameEl.title = `Fly to ${bm.name}`;
      nameEl.onclick = () => {
        flyToBookmark(bm);
        closeDropdown();
      };

      const delBtn = document.createElement('button');
      delBtn.className = 'bookmark-delete';
      delBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12"><line x1="3" y1="3" x2="9" y2="9" stroke="currentColor" stroke-width="1.5"/><line x1="9" y1="3" x2="3" y2="9" stroke="currentColor" stroke-width="1.5"/></svg>';
      delBtn.title = 'Remove';
      delBtn.onclick = (e) => {
        e.stopPropagation();
        deleteBookmark(bm.id);
        renderDropdown(container);
      };

      row.appendChild(nameEl);
      row.appendChild(delBtn);
      container.appendChild(row);
    }
  }

  // Save current view button
  const saveBtn = document.createElement('button');
  saveBtn.className = 'bookmark-save-btn';
  saveBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12"><line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" stroke-width="1.5"/><line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" stroke-width="1.5"/></svg> Save current view';
  saveBtn.onclick = () => {
    // Replace save button with input
    const inputRow = document.createElement('div');
    inputRow.className = 'bookmark-input-row';

    const input = document.createElement('input');
    input.className = 'bookmark-input';
    input.type = 'text';
    input.placeholder = 'Location name…';
    input.maxLength = 40;

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'bookmark-confirm-btn';
    confirmBtn.textContent = 'Save';
    confirmBtn.onclick = () => {
      const name = input.value.trim();
      if (name) {
        addBookmark(name);
        renderDropdown(container);
      }
    };

    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        confirmBtn.click();
      } else if (e.key === 'Escape') {
        renderDropdown(container);
      }
    };

    inputRow.appendChild(input);
    inputRow.appendChild(confirmBtn);
    saveBtn.replaceWith(inputRow);
    input.focus();
  };
  container.appendChild(saveBtn);
}

/** Toggle the bookmark dropdown. Called from map control button. */
export function toggleBookmarkDropdown(): void {
  if (dropdown) {
    closeDropdown();
    return;
  }

  const btn = document.querySelector('.bookmark-tool-btn');
  if (!btn) return;

  dropdown = document.createElement('div');
  dropdown.className = 'bookmark-dropdown';

  // Position below the button
  const rect = btn.getBoundingClientRect();
  dropdown.style.position = 'fixed';
  dropdown.style.top = `${rect.bottom + 4}px`;
  dropdown.style.right = `${window.innerWidth - rect.right}px`;

  renderDropdown(dropdown);
  document.body.appendChild(dropdown);

  // Close on outside click (delayed to avoid immediate trigger)
  setTimeout(() => {
    document.addEventListener('click', onOutsideClick);
  }, 0);
}
